// No migrations: the local db is a cache of the `data` branch, so when the
// schema changes it is dropped and rebuilt, and the next sync pulls every note
// back. Bump SCHEMA_VERSION with every change to schema.sql. `settings` is
// kept, and so are the notes the remote doesn't have yet: they're set aside
// in `kept_notes` and put back into the new table, their columns that
// still exist carried over, the new ones left to their defaults - so a new
// column needs one.
import type { Database } from './database';
import { prepareQuery } from './query';
import schema from './schema.sql?raw';

export const SCHEMA_VERSION = 5;

const KEPT = ['settings', 'kept_notes'];

const tables = async (DB: Database) => {
  const { results } = await prepareQuery(
    DB,
    `SELECT name FROM sqlite_master
     WHERE type = 'table' AND name NOT LIKE 'sqlite_%'`,
  ).bind().all<{ name: string }>();
  return results.map(t => t.name);
};

const columns = async (DB: Database, table: string) => {
  const { results } = await prepareQuery(
    DB,
    `SELECT name FROM pragma_table_info(:table)`,
  ).bind({ table }).all<{ name: string }>();
  return results.map(c => c.name);
};

// Puts the set-aside notes back, in a transaction of its own after the
// rebuild's. Run on every start, so one cut short between the two still
// finishes.
const restoreKept = async (DB: Database) => {
  if (!(await tables(DB)).includes('kept_notes')) return;
  const target = new Set(await columns(DB, 'notes'));
  const shared = (await columns(DB, 'kept_notes'))
    .filter(c => target.has(c))
    .map(c => `"${c}"`)
    .join(', ');
  await DB.script(
    `INSERT INTO notes (${shared}) SELECT ${shared} FROM kept_notes;
     DROP TABLE kept_notes;`,
  );
};

export async function ensureSchema(DB: Database) {
  await restoreKept(DB);
  const row = await prepareQuery(DB, `PRAGMA user_version`)
    .bind().first<{ user_version: number }>();
  if (row?.user_version === SCHEMA_VERSION) return;

  const existing = await tables(DB);
  // a db from before sync has nothing to keep
  const keep = existing.includes('notes')
      && (await columns(DB, 'notes')).includes('dirty')
    ? [`CREATE TABLE kept_notes AS SELECT * FROM notes WHERE dirty = 1;`]
    : [];
  const drops = existing
    .filter(t => !KEPT.includes(t))
    .map(t => `DROP TABLE "${t}";`);

  // one transaction: a failed rebuild leaves the old db and version intact
  await DB.script(
    [...keep, ...drops, schema, `PRAGMA user_version = ${SCHEMA_VERSION};`]
      .join('\n'),
  );
  await restoreKept(DB);
}
